import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

/**
 * Trang Chính sách quyền riêng tư — công khai, không yêu cầu đăng nhập.
 * Dùng URL production (ví dụ https://your-domain.com/privacy-policy) trong Google Cloud OAuth consent screen.
 */
const PrivacyPolicy = () => {
  const lastUpdated = "21 tháng 3, 2026";

  return (
    <div className="min-h-screen bg-muted/40 text-foreground">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            Về trang đăng nhập
          </Link>
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Shield className="h-4 w-4 text-primary shrink-0" aria-hidden />
            Report Vault
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <article className="rounded-lg border bg-card p-6 shadow-sm sm:p-8">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Chính sách quyền riêng tư
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Cập nhật lần cuối: {lastUpdated}
          </p>

          <div className="mt-8 space-y-8 text-base leading-relaxed text-muted-foreground [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6">
            <section>
              <h2>1. Giới thiệu</h2>
              <p className="mt-2">
                Report Vault (“Ứng dụng”) là hệ thống quản lý và tóm tắt báo cáo do{" "}
                <strong className="font-medium text-foreground">EcoTech Vietnam</strong> vận hành.
                Chính sách này mô tả cách chúng tôi thu thập, sử dụng, lưu trữ và bảo vệ thông tin cá nhân
                khi bạn sử dụng Ứng dụng, bao gồm khi đăng nhập bằng tài khoản Google.
              </p>
            </section>

            <section>
              <h2>2. Phạm vi áp dụng</h2>
              <p className="mt-2">
                Chính sách áp dụng cho người dùng truy cập Ứng dụng qua trình duyệt hoặc thiết bị được hỗ trợ,
                và cho dữ liệu được xử lý trong quá trình xác thực, sử dụng tính năng nội bộ và lưu trữ liên quan
                đến tài khoản của bạn.
              </p>
            </section>

            <section>
              <h2>3. Dữ liệu chúng tôi thu thập</h2>
              <p className="mt-2">
                Tùy tính năng bạn sử dụng, chúng tôi có thể xử lý các loại thông tin sau:
              </p>
              <ul>
                <li>
                  <strong className="font-medium text-foreground">Thông tin tài khoản đăng nhập:</strong> khi bạn chọn
                  “Đăng nhập với Google”, Google có thể cung cấp cho hệ thống của chúng tôi các định danh cần thiết
                  để xác thực (ví dụ địa chỉ email, họ tên hiển thị, ảnh đại diện nếu có và mã định danh người dùng),
                  theo phạm vi quyền bạn đồng ý trên màn hình đồng ý của Google.
                </li>
                <li>
                  <strong className="font-medium text-foreground">Dữ liệu sử dụng Ứng dụng:</strong> nội dung báo cáo,
                  cấu hình nhóm/quyền, nhật ký thao tác cần thiết cho vận hành và hỗ trợ (nếu có).
                </li>
                <li>
                  <strong className="font-medium text-foreground">Dữ liệu kỹ thuật:</strong> thông tin trình duyệt,
                  địa chỉ IP, cookie hoặc lưu trữ cục bộ cần cho phiên đăng nhập và bảo mật cơ bản.
                </li>
              </ul>
            </section>

            <section>
              <h2>4. Mục đích sử dụng</h2>
              <p className="mt-2">Chúng tôi xử lý dữ liệu để:</p>
              <ul>
                <li>Cung cấp và duy trì chức năng đăng nhập, phân quyền và quản lý người dùng trong tổ chức.</li>
                <li>Cho phép tải lên, xem, tóm tắt và quản lý báo cáo theo vai trò được cấp.</li>
                <li>Bảo đảm an toàn hệ thống, phòng chống lạm dụng và tuân thủ nghĩa vụ pháp lý khi có yêu cầu.</li>
              </ul>
            </section>

            <section>
              <h2>5. Cơ sở pháp lý và chia sẻ</h2>
              <p className="mt-2">
                Việc xử lý dựa trên sự đồng ý của bạn (đăng nhập Google), thực hiện hợp đồng/hợp tác nội bộ với tổ chức
                bạn thuộc về, hoặc lợi ích hợp pháp trong việc vận hành an toàn dịch vụ. Chúng tôi không bán dữ liệu
                cá nhân. Dữ liệu có thể được chia sẻ với nhà cung cấp hạ tầng tin cậy (ví dụ dịch vụ lưu trữ và xác thực)
                theo hợp đồng xử lý dữ liệu và chỉ trong phạm vi cần thiết.
              </p>
            </section>

            <section>
              <h2>6. Lưu trữ và bảo mật</h2>
              <p className="mt-2">
                Dữ liệu được lưu trữ trên hạ tầng đám mây với các biện pháp kiểm soát truy cập, mã hóa kết nối (HTTPS)
                và phân quyền theo tài khoản. Thời gian lưu trữ phụ thuộc mục đích nghiệp vụ và yêu cầu pháp lý;
                dữ liệu không còn cần thiết sẽ được xóa hoặc ẩn danh hóa theo quy trình nội bộ.
              </p>
            </section>

            <section>
              <h2>7. Quyền của bạn</h2>
              <p className="mt-2">
                Tùy luật hiện hành, bạn có thể có quyền truy cập, chỉnh sửa, xóa, hạn chế xử lý, phản đối hoặc yêu cầu
                sao chép dữ liệu cá nhân. Để thực hiện, vui lòng liên hệ qua email dưới đây. Chúng tôi sẽ phản hồi
                trong thời gian hợp lý.
              </p>
            </section>

            <section>
              <h2>8. Trẻ em</h2>
              <p className="mt-2">
                Ứng dụng hướng tới người dùng trong môi trường làm việc. Chúng tôi không cố ý thu thập dữ liệu từ trẻ em
                dưới độ tuổi theo luật địa phương áp dụng.
              </p>
            </section>

            <section>
              <h2>9. Thay đổi chính sách</h2>
              <p className="mt-2">
                Chúng tôi có thể cập nhật chính sách này. Ngày “Cập nhật lần cuối” ở đầu trang sẽ được điều chỉnh tương ứng.
                Việc tiếp tục sử dụng Ứng dụng sau khi thay đổi có hiệu lực đồng nghĩa bạn chấp nhận phiên bản mới,
                trừ khi luật yêu cầu hình thức đồng ý khác.
              </p>
            </section>

            <section>
              <h2>10. Liên hệ</h2>
              <p className="mt-2">
                Mọi câu hỏi về quyền riêng tư hoặc yêu cầu liên quan dữ liệu cá nhân, vui lòng gửi email tới:{" "}
                <a
                  href="mailto:ytrinh@eco-tech.vn"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  ytrinh@eco-tech.vn
                </a>
                .
              </p>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
